import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';

import type { PopupData, Service } from 'webapp-types';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import PopupInnerContextComponent from 'webapp/components/popup/inner/context';

import MetricSetup from 'webapp/models-custom/metric-setup';

export interface DataAction {
	id: DataActionId;
	label: string;
	icon: IconName;
}

type DataActionId = 'viewRawData' | 'viewMetricInsights' | 'viewGoalDetails';

export default class BlockContentChartPopupComponent extends PopupInnerContextComponent<PopupData.Block.Data> {
	@service('metric-raw-data') declare metricRawDataService: Service.MetricRawData;
	@service('metric-details') declare metricDetailsService: Service.MetricDetails;
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;
	@service('navigation') declare navigationService: Service.Navigation;

	get openMetricInsightsDrawerOrigin() {
		const block = this.popupData.dataSelect.block;

		if (block?.isFromBlockPreview) {
			return 'datablock_explorer';
		}

		const section = this.navigationService.activatedSection;
		switch (section) {
			case 'boards':
				return 'databoards';
			case 'reports':
				return 'report';
			case 'teams':
				return 'teams';
			default:
				return section || 'block';
		}
	}

	get dateRange() {
		return this.popupData.dataSelect.dateRange;
	}

	@action
	doAction(actionId: DataActionId): void {
		switch (actionId) {
			case 'viewRawData':
				this.viewRawData();
				break;
			case 'viewMetricInsights':
				this.openMetricInsightsDrawer();
				break;
			case 'viewGoalDetails':
				this.viewGoalDetails();
				break;
		}
	}

	@action
	viewGoalDetails() {
		const key = this.popupData.point.metricSelectionData?.metricSetupData?.key;
		if (key) {
			this.mmGoalsService.openGoalInsightsDrawerByMetricKey(key);
		}
		this.close();
	}

	@action
	viewRawData() {
		this.metricRawDataService.requestDrillDownDataForSelectedPoint(
			this.popupData.dataSelect,
			this.popupData.point,
			this.popupData.event,
		);

		this.close();
	}

	@action
	openMetricInsightsDrawer() {
		const { dataSelect, point } = this.popupData;
		const metricSetup = new MetricSetup(dataSelect.metricSetupData || undefined);

		const { dimensionValues, unit } = point.metricSelectionData || {};
		if (dimensionValues) {
			metricSetup.setFiltersFromDimensionsAndValuesMapAndUnit(dimensionValues, unit);
		}

		if (unit) {
			metricSetup.unitName = unit;
		}

		this.metricDetailsService.openMetricDetailsDrawer(
			{
				metricSetup,
				dateRange: this.dateRange,
			},
			this.openMetricInsightsDrawerOrigin,
		);
	}
	<template>
		<div class="body">
			{{#each this.popupData.actions as |dataAction|}}
				<a {{on "click" (fn this.doAction dataAction.id)}}>
					<CommonUiIconComponent @name={{dataAction.icon}} @size="sm" />
					{{dataAction.label}}
				</a>
			{{/each}}
		</div>
	</template>
}
